import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  RequestHandler,
  TypePolicies,
  TypePolicy,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { getMainDefinition } from '@apollo/client/utilities'
import { createConsumer } from '@rails/actioncable'
import { createUploadLink } from 'apollo-upload-client'
import axios from 'axios'
import { Environment } from 'config/environment'
import { sha256 } from 'crypto-hash'
import { default as abstractTypes } from 'generated/resources/abstract-possible-types.generated'
import {
  RESOURCE_REGISTRY_FINDERS,
  RESOURCE_REGISTRY_NAMESPACES,
  RESOURCE_REGISTRY_TYPES_WITH_POLICIES,
} from 'generated/resources/namespaces'
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink'
import { sessionExpired } from 'initializers/axiosInterceptors'
import { isString, merge } from 'lodash'

import { buildRealtimeUrl, buildUrl } from 'modules/core/lib/buildUrl'
import {
  isFactorialIdUser,
  refreshFactorialIdToken,
} from 'modules/core/lib/factorialIdTokenRefresh'

import { bankingTypePolicies } from 'modules/banking/lib/typePolicies'
import { inboxTasksTypePolicies } from 'modules/inbox_tasks/lib/typePolicies'
import { jobCatalogTypePolicies } from 'modules/job_catalog/lib/typePolicies'
import { typePolicies as performanceProcessTypePolicies } from 'modules/performance_process/lib/typePolicies'
import { projectManagementTypePolicies } from 'modules/project_management/lib/typePolicies'

import buildPaginationPolicy from './buildPaginationPolicy'

const buildNamespacePolicies: () => TypePolicies = () => {
  const fields = RESOURCE_REGISTRY_NAMESPACES.reduce((acc, actual) => {
    return {
      ...acc,
      [actual]: { merge: true },
    }
  }, {})

  return {
    Query: {
      fields: fields,
    },
    ...RESOURCE_REGISTRY_NAMESPACES.reduce((acc, actual) => {
      return {
        ...acc,
        [actual]: {
          fields: {
            _policies: { merge: true },
          },
        },
      }
    }, {}),
  }
}

const buildPoliciesForTypesWithPolicies = () => {
  return RESOURCE_REGISTRY_TYPES_WITH_POLICIES.reduce((acc, actual) => {
    return {
      ...acc,
      [actual]: { fields: { _policies: { merge: true } } },
    }
  }, {})
}

/* This lists all the fields with the "identity" directive and implements Apollo's Cache
   Redirects so the data is already available using `resource(id: 1)` if we have it,
   instead of performing the query */
const buildCacheRedirects = () => {
  return RESOURCE_REGISTRY_FINDERS.reduce<TypePolicies>((acc, finder) => {
    const typePolicy: TypePolicy = {
      fields: {
        [finder.fieldName]: {
          read(_, { args, toReference }) {
            return toReference({
              __typename: finder.typeName,
              id: args?.id,
            })
          },
        },
      },
    }

    return merge(acc, {
      [finder.rootTypeName]: typePolicy,
    })
  }, {})
}

const typePolicies: TypePolicies = merge(
  buildNamespacePolicies(),
  buildPaginationPolicy(),
  buildCacheRedirects(),
  performanceProcessTypePolicies,
  bankingTypePolicies,
  projectManagementTypePolicies,
  inboxTasksTypePolicies,
  jobCatalogTypePolicies,
  buildPoliciesForTypesWithPolicies()
)

export const buildClient = async (environment: Environment) => {
  const { cache, concatLink } = buildBaseConfig(environment)

  const createGraphQLCable = async () => {
    try {
      const { data } = await axios({
        method: 'POST',
        url: buildUrl('/authentication/realtime/token'),
        withCredentials: true,
      })

      return createConsumer(buildRealtimeUrl(`/cable?token=${data.token}`))
    } catch (error) {
      return null
    }
  }

  const cable = await createGraphQLCable()

  const link = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    cable ? new ActionCableLink({ cable }) : ApolloLink.from([]),
    concatLink
  )

  const client = new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
    connectToDevTools: true,
  })

  return client
}

export const buildSyncClient = (environment: Environment) => {
  const { cache, concatLink } = buildBaseConfig(environment)

  return new ApolloClient({
    link: concatLink,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
    connectToDevTools: true,
  })
}

const buildBaseConfig = (environment: Environment) => {
  const cache = new InMemoryCache({
    addTypename: true,
    typePolicies,
    possibleTypes: abstractTypes.possibleTypes,
  })

  // FIXME: There is an incompatibility between apollo-upload-client and @apollo/client.
  //        In order to support TypeScript 5.5 we need to cast the return type of createUploadLink
  const httpUploadLink = createUploadLink({
    uri: buildUrl('/graphql'),
    credentials: 'include',
    headers: {
      'X-Deployment-Phase': environment.deploymentPhase,
      'X-Factorial-Version': environment.releaseVersion,
      'X-Factorial-Origin': 'web',
    },
    fetch: (uri, options) => {
      const { operationName = '' } =
        options && isString(options.body) ? JSON.parse(options.body.toString()) : {}

      if (!operationName) {
        return fetch(uri, options)
      }

      return fetch(`${uri}?${operationName}`, options)
    },
  }) as unknown as ApolloLink | RequestHandler

  const persistedQueriesLink = ['POST', 'GET'].includes(environment.graphqlPersistedQueriesMode)
    ? createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: environment.graphqlPersistedQueriesMode === 'GET',
      })
    : null

  // BOOKKEEPERS
  const bookkeeperMiddleware = new ApolloLink((operation, forward) => {
    if (operation.getContext().isBookkeeperView) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'bookkeeper-view': 'true',
        },
      }))
    }

    return forward(operation)
  })

  // BROKEN VERSION CHECK
  const brokenVersionMiddleware = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext()
      const {
        response: { headers },
      } = context

      if (headers && headers.get('x-factorial-broken-version') === 'force-reload') {
        window.location.reload()
      }

      if (headers && headers.get('x-factorial-broken-version') === 'force-invalidation') {
        cache.gc({
          resetResultCache: true,
          resetResultIdentities: true,
        })
      }

      return response
    })
  })

  const errorLink = onError(({ networkError, operation, forward }) => {
    if (networkError && networkError.message.match(/status code 401/)) {
      // Try to refresh token for Factorial ID users
      if (isFactorialIdUser()) {
        // Return an Observable that handles the async token refresh
        return new Observable((observer) => {
          refreshFactorialIdToken()
            .then((refreshed) => {
              if (refreshed) {
                // Retry the operation with the new token
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                }
                forward(operation).subscribe(subscriber)
              } else {
                sessionExpired(environment)
                observer.complete()
              }
            })
            .catch((_) => {
              sessionExpired(environment)
              observer.complete()
            })
        })
      }

      // If not a Factorial ID user, redirect to login
      sessionExpired(environment)
    }
  })

  return {
    concatLink: ApolloLink.from(
      [
        errorLink,
        bookkeeperMiddleware,
        brokenVersionMiddleware,
        persistedQueriesLink,
        httpUploadLink,
      ].filter(Boolean)
    ),
    cache,
  }
}
